import { apiClient, extractApiError } from './api';
import { ApiResponse, BenefitCategoryEnum, User, UserBenefit } from '@/types';

// Backend /benefits response shape (BenefitSnapshotData from gestionnaire snapshot).
interface BackendBenefit {
  id: string;
  name: string;
  description: string;
  type: string;
  coverageRate: number;
  annualLimit: number;
  sessionLimit?: number;
  waitingPeriod?: number;
  usedAmount?: number;
  remainingAmount?: number;
  hasAnnualLimit?: boolean;
}

interface BackendCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  benefits: BackendBenefit[];
}

export type CoverageStatus = 'active' | 'suspended' | 'terminated' | 'no_contract';

interface BackendBenefitsResponse {
  status?: CoverageStatus;
  statusMessage?: string;
  categories: BackendCategory[];
  metadata?: {
    contractId: string;
    contractName: string;
    policyNumber: string;
    generatedAt: string;
    validUntil?: string;
  };
}

export interface UserBenefitsResult {
  status: CoverageStatus;
  statusMessage?: string;
  benefits: UserBenefit[];
}

// Backend `type` (consultation, pharmacy, hospital, dental, optical, specialist, lab, other)
// → frontend BenefitCategoryEnum.
const BACKEND_TYPE_TO_CATEGORY: Record<string, BenefitCategoryEnum> = {
  consultation: BenefitCategoryEnum.CONSULTATION,
  specialist: BenefitCategoryEnum.CONSULTATION,
  pharmacy: BenefitCategoryEnum.PHARMACY,
  hospital: BenefitCategoryEnum.HOSPITALIZATION,
  dental: BenefitCategoryEnum.DENTAL,
  optical: BenefitCategoryEnum.OPTICAL,
  lab: BenefitCategoryEnum.OTHER,
  other: BenefitCategoryEnum.OTHER,
};

function flattenCategories(categories: BackendCategory[]): UserBenefit[] {
  const result: UserBenefit[] = [];
  for (const category of categories) {
    for (const b of category.benefits) {
      result.push({
        id: b.id,
        category: BACKEND_TYPE_TO_CATEGORY[b.type] ?? BenefitCategoryEnum.OTHER,
        name: b.name,
        description: b.description || category.description || '',
        coveragePercentage: b.coverageRate,
        annualCeiling: b.hasAnnualLimit && b.annualLimit > 0 ? b.annualLimit : undefined,
        waitingPeriod: b.waitingPeriod && b.waitingPeriod > 0 ? b.waitingPeriod : undefined,
        icon: category.icon,
        consumedAmount: b.usedAmount ?? 0,
        remainingAmount: b.remainingAmount,
      });
    }
  }
  return result;
}

class BenefitService {
  async getCategories(): Promise<BenefitCategoryEnum[]> {
    return Object.values(BenefitCategoryEnum);
  }

  async getUserBenefits(): Promise<UserBenefitsResult> {
    try {
      const response = await apiClient.get<ApiResponse<BackendBenefitsResponse>>('/benefits');
      const data = response.data.data;
      return {
        status: data?.status ?? 'active',
        statusMessage: data?.statusMessage,
        benefits: data?.categories ? flattenCategories(data.categories) : [],
      };
    } catch (error) {
      throw extractApiError(error);
    }
  }

  async getFamilyMembers(): Promise<User[]> {
    try {
      const response = await apiClient.get<ApiResponse<User[]>>('/users/family');
      return response.data.data ?? [];
    } catch (error) {
      throw extractApiError(error);
    }
  }
}

export const benefitService = new BenefitService();
export default benefitService;
